// Status chips for clips and document summaries (amber = in progress, red = error, grey = local).
import type { ReactNode } from 'react';
import type { DocumentSummary } from '../../../shared/types';
import type { ViewClip, ViewSummary } from '../lib/api';
import { Icon } from './Icon';

type ChipTone = 'pending' | 'error' | 'local' | 'done' | 'due' | 'overdue' | 'neutral';

export function Chip({ tone, children, title }: { tone: ChipTone; children: ReactNode; title?: string }) {
  return (
    <span className={`chip chip--${tone}`} title={title}>
      {children}
    </span>
  );
}

/** True when ClipStatusChip shows something for the clip. */
export function hasStatusChip(clip: ViewClip): boolean {
  return clip.localPending === true || clip.status !== 'done';
}

/** Chip for one clip, or null when it is transcribed. */
export function ClipStatusChip({ clip }: { clip: ViewClip }) {
  if (clip.localPending) {
    return (
      <Chip tone="local">
        <Icon name="clock" size={14} /> Čaka na prenos
      </Chip>
    );
  }
  switch (clip.status) {
    case 'queued':
      return <Chip tone="pending">Čaka na prepis</Chip>;
    case 'processing':
      return (
        <Chip tone="pending">
          <Icon name="spinner" size={14} /> Prepisujem …
        </Chip>
      );
    case 'error':
      return (
        <Chip tone="error" title={clip.error ?? undefined}>
          <Icon name="alert" size={14} /> Napaka
        </Chip>
      );
    case 'done':
      return null;
  }
}

/** Chips for a list entry (document summary). */
export function SummaryChips({ summary }: { summary: DocumentSummary }) {
  const local = (summary as ViewSummary).localPending === true;
  return (
    <>
      {local && (
        <Chip tone="local">
          <Icon name="clock" size={14} /> Čaka na prenos
        </Chip>
      )}
      {summary.pendingCount > 0 && (
        <Chip tone="pending">
          <Icon name="spinner" size={14} /> Prepisujem …
        </Chip>
      )}
      {summary.errorCount > 0 && (
        <Chip tone="error">
          <Icon name="alert" size={14} /> Napaka
        </Chip>
      )}
    </>
  );
}
